/**
 * Admin Portal → Codes: the pieces every screen of the one Codes area shares (Codes PR-8) — the
 * page header, the sub-tabs, the admin's role (read from the server: write tools stay hidden until
 * it answers, and whenever it fails — fail closed) and the "not available" line for a read-model
 * switch that is off or a deploy still finishing.
 *
 * Web-only (the admin SPA): react-router is fine here.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SectionHeader } from '../../../components/admin/ui';
import { PartnerApiError } from '../../../services/partnersAdminService';
import { errText, hint, useAdminRole } from '../partners/PartnersShared';
import { canWriteCodes, isSuperAdmin } from './codesAreaLogic';

export const PAGE: React.CSSProperties = { padding: 24, maxWidth: 1280 };

/** The signed-in admin's role and what it may do in the Codes area (all false until the server answers). */
export function useCodesRole(): { role: string | null; loaded: boolean; writable: boolean; superAdmin: boolean } {
  const { role, loaded } = useAdminRole();
  return { role, loaded, writable: loaded && canWriteCodes(role), superAdmin: loaded && isSuperAdmin(role) };
}

export const CodesHeader: React.FC<{ title: string; subtitle?: React.ReactNode; actions?: React.ReactNode }> = ({
  title, subtitle, actions,
}) => <SectionHeader as="h1" title={title} subtitle={subtitle} actions={actions} />;

/** Tabs that are links (each its own address, so a tab can be bookmarked and the back button works). */
export const SubTabs: React.FC<{ tabs: Array<{ to: string; label: string; exact?: boolean }> }> = ({ tabs }) => {
  const { pathname } = useLocation();
  // The longest matching tab wins: `/admin/codes/all/batches/…` is the Batches tab, not All codes.
  const match = [...tabs]
    .sort((a, b) => b.to.length - a.to.length)
    .find((t) => pathname === t.to || (!t.exact && pathname.startsWith(`${t.to}/`)));
  return (
    <div role="tablist" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '4px 0 16px' }}>
      {tabs.map((t) => {
        const on = match?.to === t.to;
        return (
          <Link key={t.to} to={t.to} role="tab" aria-selected={on}
            className={`admin-btn admin-btn--${on ? 'primary' : 'secondary'} admin-btn--sm`}>
            {t.label}
          </Link>
        );
      })}
    </div>
  );
};

/** A read-model refusal in the admin's words: the switch off (503 disabled) or the deploy window (503 schema_pending). */
export function readModelError(err: unknown): string {
  if (err instanceof PartnerApiError && err.status === 503) {
    return err.code === 'disabled'
      ? 'The Codes area is switched off (FF_CODE_CATALOG_API). The partner and Summer50 pages still work.'
      : 'The code list is not set up yet — the deploy may still be finishing. Try again in a few minutes.';
  }
  return errText(err);
}

export const ErrorLine: React.FC<{ text: string | null }> = ({ text }) =>
  text ? (
    <div role="alert" style={{ background: 'var(--status-error-bg, #FEF2F2)', color: 'var(--status-error-text, #B91C1C)',
      padding: 10, borderRadius: 8, margin: '8px 0 12px', fontSize: 13 }}>{text}</div>
  ) : null;

/** The vocabulary line: which status words this screen uses (review of #4276, NIT-4). */
export const VocabNote: React.FC<{ text: string }> = ({ text }) => (
  <p data-testid="vocab-note" style={{ ...hint, marginTop: 0, marginBottom: 12 }}>{text}</p>
);
